import React from 'react';
import { Box, Button, Slider, Typography } from '@mui/material';
import { useLanguage } from '../../i18n/LanguageContext';
import { NodeCardShell } from './NodeCardShell';
import { NodeCardProps } from './NodeCardProps';

export const LightNodeCard: React.FC<NodeCardProps> = ({
  node, index, onToggle, onCommand, onSettingsChange, onEditOpen,
}) => {
  const { t } = useLanguage();
  const s = node.settings ?? {};
  const brightness = s.brightness ?? 100;
  const colorTemp  = s.colorTemp  ?? 50;

  const handleOn  = () => { if (node.state !== true)  onToggle(); };
  const handleOff = () => { if (node.state !== false) onToggle(); };

  return (
    <NodeCardShell node={node} index={index} onEditOpen={onEditOpen}>
      <Box display="flex" gap={1} mb={1.5}>
        <Button
          variant={node.state !== true ? 'contained' : 'outlined'}
          color={node.state === false ? 'error' : 'inherit'}
          size="small" onClick={handleOff} disabled={node.isLoading} sx={{ flex: 1 }}
        >
          {t.statusOff}
        </Button>
        <Button
          variant={node.state === true ? 'contained' : 'outlined'}
          color={node.state === true ? 'success' : 'inherit'}
          size="small" onClick={handleOn} disabled={node.isLoading} sx={{ flex: 1 }}
        >
          {t.statusOn}
        </Button>
      </Box>

      <Box display="flex" alignItems="center" gap={2} mb={1}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
          {t.lightBrightness}
        </Typography>
        <Slider
          aria-label={t.lightBrightness}
          value={brightness}
          min={0} max={100} step={5}
          onChange={(_, v) => onSettingsChange({ ...s, brightness: Array.isArray(v) ? v[0] : v })}
          onChangeCommitted={(_, v) => {
            const val = Array.isArray(v) ? v[0] : v;
            onSettingsChange({ ...s, brightness: val });
            onCommand('setBrightness', { value: String(val) });
          }}
          size="small" sx={{ flex: 1 }}
        />
        <Typography variant="caption" sx={{ minWidth: 36, textAlign: 'right' }}>
          {brightness}%
        </Typography>
      </Box>

      <Box display="flex" alignItems="center" gap={2}>
        <Typography variant="caption" color="text.secondary" sx={{ minWidth: 72 }}>
          {t.lightColorTemp}
        </Typography>
        <Typography variant="caption" color="text.secondary">{t.lightColorWarm}</Typography>
        <Slider
          aria-label={t.lightColorTemp}
          value={colorTemp}
          min={0} max={100} step={5}
          onChange={(_, v) => onSettingsChange({ ...s, colorTemp: Array.isArray(v) ? v[0] : v })}
          onChangeCommitted={(_, v) => {
            const val = Array.isArray(v) ? v[0] : v;
            onSettingsChange({ ...s, colorTemp: val });
            onCommand('setColorTemp', { value: String(val) });
          }}
          size="small" sx={{ flex: 1 }}
        />
        <Typography variant="caption" color="text.secondary">{t.lightColorCool}</Typography>
      </Box>
    </NodeCardShell>
  );
};
